import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import { comics, getFeaturedComics, getLatestUpdates, getTrendingComics, getPopularComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import type { Comic } from '@/mocks/comics';

function HeroSection() {
  return (
    <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20beautiful%20stylized%20Korean%20manhwa%20art%20style%20background%20with%20cherry%20blossoms%20and%20starry%20night%20sky%20soft%20gradient%20from%20deep%20coral%20to%20dark%20navy%20magical%20floating%20light%20particles%20silhouette%20of%20a%20city%20skyline%20at%20the%20bottom%20dreamy%20atmospheric%20illustration%20digital%20art&width=1800&height=900&seq=hero-bg-home-01&orientation=landscape"
        alt="虫虫漫画网 - 韩国漫画大全免费在线阅读"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      <div className="relative z-10 container-main text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading text-white mb-5 leading-tight">
          韩国漫画大全免费
          <span className="block text-2xl md:text-3xl lg:text-4xl mt-2 font-sans font-light text-white/80">
            韩漫下拉式在线阅读
          </span>
        </h1>
        <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
          虫虫漫画网汇聚海量优质韩漫资源，支持流畅的下拉式阅读体验。
          恋爱、奇幻、动作、悬疑等丰富类型，每日更新，免费阅读，开启你的漫画阅读旅程。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/all"
            className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            开始阅读 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 rounded-full border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
          >
            了解更多
          </Link>
        </div>
        <div className="flex items-center justify-center gap-8 mt-10">
          {[
            { label: '漫画总数', value: `${comics.length}+` },
            { label: '每日更新', value: '20+' },
            { label: '累计读者', value: '500万+' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
              <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="py-8 bg-background-50">
      <div className="container-main">
        <div className="flex items-center justify-center flex-wrap gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-500 hover:text-white transition-all duration-200 whitespace-nowrap"
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = getFeaturedComics();
  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="container-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">精选推荐</h2>
            <p className="text-sm text-foreground-500 mt-1">编辑精选，不容错过的优质韩漫</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {featured.map((comic) => (
            <ComicCard key={comic.id} comic={comic} variant="default" />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latest = getLatestUpdates(12);
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="container-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">最近更新</h2>
            <p className="text-sm text-foreground-500 mt-1">每日新鲜，持续更新中</p>
          </div>
          <Link
            to="/category/all"
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {latest.map((comic) => (
            <ComicCard key={comic.id} comic={comic} variant="default" />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrendingSection() {
  const trending = getTrendingComics();
  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="container-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">热门趋势</h2>
            <p className="text-sm text-foreground-500 mt-1">大家都在看的热门韩漫</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {trending.map((comic) => (
            <ComicCard key={comic.id} comic={comic} variant="default" />
          ))}
        </div>
      </div>
    </section>
  );
}

function PopularRanking() {
  const popular = getPopularComics(10);
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="container-main">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">人气排行榜</h2>
              <p className="text-sm text-foreground-500 mt-1">读者票选最高人气韩漫</p>
            </div>
            <div className="space-y-3">
              {popular.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="flex items-center gap-4 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors group"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    index < 3 ? 'bg-primary-500 text-white' : 'bg-secondary-200 text-secondary-700'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="w-12 h-16 rounded-md overflow-hidden flex-shrink-0">
                    <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500">
                      <span>{comic.author}</span>
                      <span className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i> {comic.rating}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <p className="text-xs text-foreground-400 mt-1">{(comic.views / 10000).toFixed(0)}万阅读</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:w-80">
            <div className="bg-background-100 rounded-xl p-6 sticky top-24">
              <h3 className="text-lg font-heading text-foreground-950 mb-2">虫虫漫画网</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                韩国漫画大全免费在线阅读平台，支持韩漫下拉式阅读体验。海量优质韩漫资源，每日持续更新。
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-fill text-primary-500"></i>
                  免费阅读所有漫画
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-fill text-primary-500"></i>
                  下拉式流畅阅读体验
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-fill text-primary-500"></i>
                  每日更新热门连载
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-fill text-primary-500"></i>
                  全设备无缝适配
                </div>
              </div>
              <Link
                to="/about"
                className="block mt-5 text-center py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                了解更多关于我们
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-14 md:py-20 bg-background-950">
      <div className="container-main text-center">
        <h2 className="text-2xl md:text-3xl font-heading text-white mb-3">
          准备好开始你的漫画阅读之旅了吗？
        </h2>
        <p className="text-foreground-400 text-sm md:text-base max-w-xl mx-auto mb-7">
          加入虫虫漫画网，探索海量韩国漫画资源，享受流畅的下拉式在线阅读体验。
        </p>
        <Link
          to="/category/all"
          className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
        >
          立即开始阅读 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '虫虫漫画网',
    url: 'https://www.chongchongmanhua.com',
    description: '韩国漫画大全免费在线阅读平台，提供韩漫下拉式阅读体验，海量优质韩漫资源每日更新。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.chongchongmanhua.com/category/all?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '虫虫漫画网',
    url: 'https://www.chongchongmanhua.com',
    description: '韩国漫画大全免费在线阅读平台',
    sameAs: [
      'https://weibo.com/chongchongmanhua',
      'https://www.chongchongmanhua.com',
    ],
  };

  return (
    <>
      <SEOHead
        title="韩国漫画大全免费-韩漫下拉式在线阅读"
        description="虫虫漫画网提供韩国漫画大全免费在线阅读，支持韩漫下拉式阅读体验。海量韩漫资源每日更新，涵盖恋爱、奇幻、动作、悬疑等多种类型，随时随地畅享无间断的漫画阅读旅程。"
        keywords="韩国漫画,韩漫,免费漫画,下拉式阅读,虫虫漫画网,漫画在线阅读,韩国漫画大全"
        canonicalUrl="https://www.chongchongmanhua.com/"
        schema={{ ...homepageSchema, ...orgSchema }}
      />
      <Navbar />
      <main>
        <HeroSection />
        <CategoryNav />
        <FeaturedSection />
        <LatestSection />
        <TrendingSection />
        <PopularRanking />
        <CTASection />
      </main>
      <Footer />
    </>
  );
}